import type { LeadStage } from '@/types/lead';
import { isDisqualifiedStage } from './disqualified-stage';

/**
 * What a stage is, as one value.
 *
 * The row spreads this across three booleans, and two of them overlap:
 * a disqualified stage carries `isLost` as well, because it *is* a loss — it
 * just isn't a competitive one. Nothing but `isDisqualified` separates the two.
 *
 * Collapsing that into a single mode is what lets a form own it. Editing three
 * booleans independently permits states the API refuses (disqualified-but-open)
 * and states that mean nothing (won-and-lost); a four-way choice cannot express
 * either.
 *
 * Lived in two components before this, each with its own three-mode copy that
 * knew nothing about disqualification.
 */
export const TERMINAL_MODES = ['open', 'won', 'lost', 'disqualified'] as const;

export type TerminalMode = (typeof TERMINAL_MODES)[number];

export const TERMINAL_MODE_LABELS: Record<TerminalMode, string> = {
  open: 'Open',
  won: 'Converted',
  lost: 'Lost',
  disqualified: 'Disqualified',
};

/**
 * The half-sentence that follows the label when a picker has room for it.
 *
 * Keyed by mode alongside the labels so an added terminal cannot appear in the
 * filter bar and the schema while going missing from the one dropdown that
 * creates it — which is the shape of the bug this module was extracted to fix.
 */
export const TERMINAL_MODE_DESCRIPTIONS: Record<TerminalMode, string> = {
  open: 'still being worked',
  won: 'closed as a sale',
  lost: 'closed, no sale',
  disqualified: 'never real work',
};

/**
 * Which terminal a stage is.
 *
 * `isWon` first: won and disqualified are mutually exclusive on the row, and
 * the API refuses the pairing, so the order between them only decides what a
 * corrupt row reads as — won is the safer reading, being the one a tenant
 * notices immediately.
 *
 * `isDisqualified` before `isLost`, because a disqualified stage sets both.
 * That order is not a near-miss: reversed, every junk column classifies as a
 * plain loss, the Disqualified filter matches nothing, the badge reads "Lost",
 * and editing such a stage silently rewrites it into one.
 *
 * Asked via `isDisqualifiedStage` rather than reading the field, so this agrees
 * with the routing. That helper prefers the flag and falls back to the name
 * only for a stage served *without* the column — a build predating it — which
 * is a distinction bare truthiness cannot make.
 *
 * Gated on `isLost` because that helper is written for stages already filtered
 * to the lost ones, and its name fallback does not re-check. Ungated, an *open*
 * stage a tenant called "Disqualified — pending review", served by a build
 * without the column, would read as the junk terminal here and be saved back
 * with `isLost: true` — closing a column that was still being worked.
 */
export function getTerminalMode(stage: LeadStage): TerminalMode {
  if (stage.isWon) return 'won';
  if (stage.isLost && isDisqualifiedStage(stage)) return 'disqualified';
  if (stage.isLost) return 'lost';
  return 'open';
}

/**
 * The row flags a mode implies — all three, every time.
 *
 * `isDisqualified` is emitted even as `false`. The API merges a patch onto the
 * stored row, so omitting it while moving a stage off Disqualified leaves the
 * stored `true` in place: the merged row is then disqualified-but-open, which
 * the API refuses. A tenant hits that as an unexplained rejection when trying
 * to repurpose the Disqualified column they were seeded with.
 */
export function terminalFlags(mode: TerminalMode): {
  isWon: boolean;
  isLost: boolean;
  isDisqualified: boolean;
} {
  return {
    isWon: mode === 'won',
    isLost: mode === 'lost' || mode === 'disqualified',
    isDisqualified: mode === 'disqualified',
  };
}
